import * as helios from "@hyperionbt/helios";
import * as Sentry from "@sentry/nextjs";
import { SecondaryOfferEntry } from "kreate-common/modules/business-types";
import { assert$DisplayableError } from "kreate-common/modules/displayable-error";
import CircularProgress$Indeterminate from "kreate-common/modules/kreate-ui/components/CircularProgress/components/CircularProgress$Indeterminate";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import { parseKreateUtxoOffer } from "kreate-common/modules/utxo-parser/by-type/offer";
import { parseKreateUtxoProtocolParams } from "kreate-common/modules/utxo-parser/by-type/protocol-params";
import { buildTx$AcceptSecondaryOffer } from "kreate-protocol/offchain/tx/acceptOffer";
import { httpGet$GetGalleryArtwork } from "kreate-server/commands/GetGalleryArtwork/fetcher";
import { httpGet$GetProtocolParams } from "kreate-server/commands/GetProtocolParams/fetcher";
import { MdFavorite } from "react-icons/md";

import { getNumEditionsInWallet } from "../../../../PageArtwork/containers/ArtworkDetailsViewer/containers/PanelPrimaryAcquisition/containers/MethodMarketplace/utils";
import ModalAcceptOffer, {
  ModalAcceptOffer$SubmitEvent,
} from "../modals/ModalAcceptOffer";

import { waitForUtxo } from "@/modules/common-utils/async";
import { formatLovelaceAmount$Compact } from "@/modules/common-utils/format";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { waitModal } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import ModalProgressV2 from "@/modules/kreate-modals/modals/ModalProgressV2";
import { anticipateSteps } from "@/modules/kreate-modals/modals/ModalProgressV2/utils";
import ModalSuccess from "@/modules/kreate-modals/modals/ModalSuccess";

const steps = anticipateSteps({
  fetchingProtocolParams: 1000,
  buildingTransaction: 5000,
  waitingForSignatureAndSubmission: 8000,
  waitingForConfirmation: 23000,
});

type Params = {
  cip30Wallet: helios.Cip30Wallet;
  offer: SecondaryOfferEntry;
  txMessage: string;
};

export async function* runFlow$AcceptOffer({
  cip30Wallet,
  offer,
  txMessage,
}: Params) {
  const addBreadcrumb = (msg: string) => {
    const terms = ["runFlow$AcceptOffer", Date.now(), encodeURIComponent(msg)];
    Sentry.addBreadcrumb({ message: terms.join(" - ") });
  };

  addBreadcrumb("precomputing...");
  yield (
    <ModalV2.Root style={{ backgroundColor: "transparent" }} open>
      <Flex.Row padding="48px" justifyContent="center" alignItems="center">
        <CircularProgress$Indeterminate size="96px" />
      </Flex.Row>
    </ModalV2.Root>
  );

  const parsedUtxo = parseKreateUtxoOffer(offer.utxo);
  const assetId = parsedUtxo.datumOffer.assetId;

  const [galleryArtwork, numEditionsInWallet, utxoProtocolParams] =
    await Promise.all([
      httpGet$GetGalleryArtwork("/api/v1/collector-gallery/artwork", {
        assetId,
      }),
      getNumEditionsInWallet(cip30Wallet, assetId),
      httpGet$GetProtocolParams("/api/v1/utxo/protocol-params"),
    ]);

  const datumProtocolParams =
    parseKreateUtxoProtocolParams(utxoProtocolParams).datumProtocolParams;

  assert$DisplayableError(numEditionsInWallet > 0, {
    title: "Asset not found",
    description:
      "The currently connected wallet does not contain any Editions of this Artwork.",
    cause: { numEditionsInWallet },
  });

  addBreadcrumb("waiting for user inputs...");
  const { numEditions } = yield* waitModal<ModalAcceptOffer$SubmitEvent>(
    (resolve, reject) => (
      <ModalAcceptOffer
        open
        galleryArtwork={galleryArtwork}
        numEditionsInWallet={numEditionsInWallet}
        offer={offer}
        onSubmit={(event) => resolve(event)}
        onCancel={() => reject(null)}
      />
    )
  );

  addBreadcrumb("building transaction...");
  yield (
    <ModalProgressV2
      open
      content="Building transaction..."
      ratio={steps.buildingTransaction.ratio}
      expectedDuration={steps.buildingTransaction.duration}
    />
  );

  const { tx } = await buildTx$AcceptSecondaryOffer(
    cip30Wallet,
    offer.utxo,
    numEditions,
    new helios.Address(datumProtocolParams.treasuryAddress),
    txMessage
  );

  addBreadcrumb("waiting for signature and submission...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for signature and submission..."
      ratio={steps.waitingForSignatureAndSubmission.ratio}
      expectedDuration={steps.waitingForSignatureAndSubmission.duration}
    />
  );

  const signatures = await cip30Wallet.signTx(tx);
  tx.addSignatures(signatures);
  const txId = await cip30Wallet.submitTx(tx);

  addBreadcrumb("waiting for confirmation...");
  yield (
    <ModalProgressV2
      open
      content="Waiting for confirmation..."
      ratio={steps.waitingForConfirmation.ratio}
      expectedDuration={steps.waitingForConfirmation.duration}
    />
  );

  await waitForUtxo(cip30Wallet, (utxo) => utxo.txId.eq(txId));
  await shamelesslyRevalidateEverything();

  addBreadcrumb("showing success message");
  yield* waitModal<void>((resolve) => (
    <ModalSuccess
      open
      icon={<MdFavorite />}
      content={`You successfully accepted the Offer of ${formatLovelaceAmount$Compact(
        parsedUtxo.datumOffer.editionPrice * numEditions
      )} for ${numEditions} Editions.`}
      onClose={resolve}
      buttons={[{ content: "Close", onClick: "close" }]}
    />
  ));

  Sentry.captureEvent({ message: "runFlow$AcceptOffer", level: "info" });
}
